"use client"
import { createContext, useContext, useId, type ReactNode } from "react"
import { LayoutGroup, motion } from "motion/react"
import type {
  BandDangerLabelPayload,
  PointHoverTooltipPayload,
} from "../../../extrapolated-graph/BandHudOverlayChannel"
import { formatCompact } from "../../../plan-a/economic-data/calculations"
import {
  getHoveredRegionId,
  type HoveredPowerItem,
} from "../../../power-bubble-chart/hoveredPowerItem"
import { regionIconById } from "../../../power-bubble-chart/powerChartIcons"
import { type PowerRegionData } from "../../../power-bubble-chart/powerBubbleChartData"
import { getLabsWorthShowing } from "../../../power-bubble-chart/distribution-of-power-tooltip/powerTooltipData"
import {
  CAPABILITY_HOVER_COPY,
  CAPABILITY_LEVEL_LADDER,
  CapabilityLadder,
} from "../capability/capabilityHoverSwap"
import {
  METRIC_HOVER_DEFINITIONS,
  type HoverableMetricId,
} from "../metrics/metricHoverSwap"
import {
  POLICY_STATUS_LABELS,
  POLICY_STATUS_ORDER,
  type PoliciesByStatus,
  type Policy,
  type PolicyStatus,
} from "../policy/policiesData"
import { POLICY_DESCRIPTIONS } from "../policy/policyDescriptions"
import { PolicyMiniSquare } from "../policy/policyHoverSwap"

// ─── Editable content ────────────────────────────────────────────────────────
// Chrome for the unified hover panels (HUD 112): every tooltip on that tab
// renders as one of these cards — cream fill, hairline border, mono header
// bar, optional "CLICK FOR DETAILS" footer.
export const UNIFIED_PANEL_STYLE = {
  background: "#fffff8",
  border: "1px solid #1a1a1a",
  borderRadius: 6,
  boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.15)",
  headerFontSize: "11px",
  bodyFontSize: "10.5px",
  bodyLineHeight: 1.5,
  statLabelFontSize: "9px",
  statValueFontSize: "13px",
  footerFontSize: "8.5px",
  // The "CLICK FOR DETAILS" footer renders in the site red (full opacity —
  // the red is the emphasis, no dimming). --race-background, not --accent:
  // cards portal to <body>, where --accent doesn't resolve to a color.
  footerColor: "var(--race-background)",
}

// The dark theme (HUD 113): solid black cards, no outline, white text, white
// hairlines. Pass theme="dark" on <UnifiedHudPanels>.
export const UNIFIED_PANEL_DARK_THEME = {
  background: "#000000",
  border: "none",
  textColorClass: "text-white",
  // Header/footer separators and inner dividers.
  lineColorClass: "border-white/25",
  // The level meter's rail and idle dots.
  meterRailClass: "bg-white/30",
  meterIdleDotBorder: "1px solid rgba(255, 255, 255, 0.6)",
}

export const CLICK_FOR_DETAILS_LABEL = "↖ Click for details"

// Copy for the capability-line panel (follows the hovered point, like
// today's tooltip). The meter shows all CAPABILITY_LEVEL_LADDER levels.
export const AI_CAPABILITIES_PANEL_COPY = {
  title: "AI Capabilities",
  speedupLabel: "R&D Speedup",
  speedupDescription:
    "How much faster AI research moves with AI assistance (1× = human-only pace).",
  levelLabel: "Agent Level",
}

// The capability card's big speedup value and the level meter's dot sizes
// (per the mockup: oversized value top-right, chunky dots on a grey rail,
// no caption row).
export const CAPABILITY_METER_STYLE = {
  speedupValueFontSize: "19px",
  dotSizePx: 11,
  currentDotSizePx: 12,
  idleDotBorderWidthPx: 1.5,
  railHeightPx: 1.5,
  // The "current" state hands off smoothly between dots as the hovered level
  // changes — size/fill/border transition instead of snapping.
  dotTransitionCss:
    "width 200ms ease-out, height 200ms ease-out, background 200ms ease-out, border 200ms ease-out",
}

// Copy for the takeover-risk panel (hovering the red gradient band).
export const AI_TAKEOVER_RISK_PANEL_COPY = {
  title: "AI Takeover Risk",
  body: "How worried we are about AI disempowering humanity if we naively scaled AI to this capability level at this point in time.",
}

export type UnifiedPanelTheme = "light" | "dark"

// Which design the capability-line hover card uses:
// "meter"        — the compact R&D-speedup card with the horizontal dot meter.
// "ladder"       — the full in-canvas takeover layout (title + subtitle,
//                  speedup top-right, every level with its one-liner) inside
//                  the floating card. The big one.
// "ladder-names" — the ladder with definitions cut: names only on the
//                  vertical rail, the current row highlighted and keeping
//                  just ITS one-liner, speedup as a footer stat row.
// "mini"         — the glance card: current level name, the horizontal dot
//                  meter, speedup. No definitions at all.
// "mini-minimal" — the mini card with the quieter meter: small filled dots on
//                  a hairline rail (no hollow borders), the current level
//                  reading from color alone.
// "mini-ink"     — the minimal meter with the gray removed (black & red
//                  only): solid black idle dots and rail, red current dot.
// "ladder-names-steady" — the ladder-names concept with wobble-proof motion:
//                  fixed-height rows, the current one-liner in a RESERVED
//                  slot below the ladder (nothing reflows), and the highlight
//                  pill moved by a card-local CSS top transition instead of a
//                  motion layout animation (which fights the card following
//                  the mouse).
// "ladder-names-clean" — the ladder-names concept with NO highlight pill:
//                  the current level reads from the red dot + bold name
//                  alone, its one-liner inline under the name (like
//                  "ladder-names"), and no motion layout animation anywhere —
//                  nothing left to wobble.
export type UnifiedCapabilityCardDesign =
  | "meter"
  | "ladder"
  | "ladder-names"
  | "mini"
  | "mini-minimal"
  | "mini-ink"
  | "ladder-names-steady"
  | "ladder-names-clean"

type UnifiedHudPanelsContextValue = {
  theme: UnifiedPanelTheme
  capabilityCard: UnifiedCapabilityCardDesign
}

const UnifiedHudPanelsContext = createContext<UnifiedHudPanelsContextValue | null>(null)

// Null outside a <UnifiedHudPanels>. Hover sources across the HUD (graph
// overlays, the power treemap, the policies panel, the metric indicators)
// read this to swap their stock tooltip content for the unified panel cards.
export const useUnifiedHudPanels = () => useContext(UnifiedHudPanelsContext)

const useIsDarkPanelTheme = () => useUnifiedHudPanels()?.theme === "dark"

// Renders no DOM of its own — wrap the whole HUD variant body. theme="dark"
// switches every card to the black no-outline treatment (HUD 113);
// capabilityCard="ladder" swaps the capability-line card for the full-ladder
// design.
export const UnifiedHudPanels = ({
  children,
  theme = "light",
  capabilityCard = "meter",
}: {
  children: ReactNode
  theme?: UnifiedPanelTheme
  capabilityCard?: UnifiedCapabilityCardDesign
}) => (
  <UnifiedHudPanelsContext.Provider value={{ theme, capabilityCard }}>
    {children}
  </UnifiedHudPanelsContext.Provider>
)

// The shared card chrome. Contents provide their own padding; `detailsHref`
// adds the "CLICK FOR DETAILS" footer as a real link (only panels whose
// subject actually opens something today get one — currently just policies
// with a source reference).
export const HudHoverPanel = ({
  title,
  children,
  detailsHref,
  // Renders the "CLICK FOR DETAILS" footer without a link — for panels whose
  // detail view exists elsewhere (production) but isn't wired up here yet.
  showDetailsFooter = false,
  widthPx,
}: {
  title?: string
  children: ReactNode
  detailsHref?: string
  showDetailsFooter?: boolean
  widthPx?: number
}) => {
  const dark = useIsDarkPanelTheme()
  const lineClass = dark
    ? UNIFIED_PANEL_DARK_THEME.lineColorClass
    : "border-black/20"
  const footerClassName = `block px-3 py-1.5 border-t ${lineClass} uppercase tracking-wide`
  const footerStyle = {
    fontSize: UNIFIED_PANEL_STYLE.footerFontSize,
    color: UNIFIED_PANEL_STYLE.footerColor,
  }
  return (
    <div
      className={`font-mono ${dark ? UNIFIED_PANEL_DARK_THEME.textColorClass : "text-black"}`}
      style={{
        background: dark
          ? UNIFIED_PANEL_DARK_THEME.background
          : UNIFIED_PANEL_STYLE.background,
        border: dark ? UNIFIED_PANEL_DARK_THEME.border : UNIFIED_PANEL_STYLE.border,
        borderRadius: UNIFIED_PANEL_STYLE.borderRadius,
        boxShadow: UNIFIED_PANEL_STYLE.boxShadow,
        width: widthPx,
      }}
    >
      {title && (
        <div
          className={`px-3 pt-2 pb-1.5 border-b ${lineClass} font-bold tracking-wide uppercase`}
          style={{ fontSize: UNIFIED_PANEL_STYLE.headerFontSize }}
        >
          {title}
        </div>
      )}
      {children}
      {detailsHref ? (
        <a
          href={detailsHref}
          target="_blank"
          rel="noopener noreferrer"
          className={footerClassName}
          style={footerStyle}
        >
          {CLICK_FOR_DETAILS_LABEL}
        </a>
      ) : showDetailsFooter ? (
        <div
          className={footerClassName}
          style={footerStyle}
        >
          {CLICK_FOR_DETAILS_LABEL}
        </div>
      ) : null}
    </div>
  )
}

const PanelStatRow = ({
  label,
  value,
  valueFontSize = UNIFIED_PANEL_STYLE.statValueFontSize,
}: {
  label: string
  value: string
  valueFontSize?: string
}) => (
  <div className="flex items-baseline justify-between gap-3">
    <span
      className="uppercase tracking-wide opacity-70 whitespace-nowrap"
      style={{ fontSize: UNIFIED_PANEL_STYLE.statLabelFontSize }}
    >
      {label}
    </span>
    <span className="font-bold text-right" style={{ fontSize: valueFontSize }}>
      {value}
    </span>
  </div>
)

const PanelBodyText = ({
  children,
  mono = false,
}: {
  children: ReactNode
  mono?: boolean
}) => (
  <div
    className={mono ? "font-mono" : "font-sans"}
    style={{
      fontSize: UNIFIED_PANEL_STYLE.bodyFontSize,
      lineHeight: UNIFIED_PANEL_STYLE.bodyLineHeight,
    }}
  >
    {children}
  </div>
)

// Dot meter over the full capability ladder, per the mockup: chunky dots on a
// grey rail, the current level filled in the accent color, the rest hollow.
// No caption row.
const CapabilityLevelMeter = ({ point }: { point: PointHoverTooltipPayload }) => {
  const dark = useIsDarkPanelTheme()
  const levelIndex = CAPABILITY_LEVEL_LADDER.findIndex(
    (l) => l.label === point.capabilityThresholdLabel,
  )
  const dotSize = CAPABILITY_METER_STYLE.dotSizePx
  return (
    <div
      className="relative mt-2"
      style={{ height: CAPABILITY_METER_STYLE.currentDotSizePx }}
    >
      <div
        className={`absolute top-1/2 -translate-y-1/2 ${dark ? UNIFIED_PANEL_DARK_THEME.meterRailClass : "bg-black/20"}`}
        style={{
          left: dotSize / 2,
          right: dotSize / 2,
          height: CAPABILITY_METER_STYLE.railHeightPx,
        }}
      />
      <div className="absolute inset-0 flex items-center justify-between">
        {CAPABILITY_LEVEL_LADDER.map((level, i) => {
          const isCurrent = i === levelIndex
          return (
            <span
              key={level.label}
              className="rounded-full"
              style={{
                width: isCurrent ? CAPABILITY_METER_STYLE.currentDotSizePx : dotSize,
                height: isCurrent ? CAPABILITY_METER_STYLE.currentDotSizePx : dotSize,
                background: isCurrent
                  ? "var(--accent)"
                  : dark
                    ? UNIFIED_PANEL_DARK_THEME.background
                    : UNIFIED_PANEL_STYLE.background,
                // 0-width border (not "none") when current, so the border
                // TRANSITIONS between states instead of snapping.
                border: isCurrent
                  ? "0px solid transparent"
                  : `${CAPABILITY_METER_STYLE.idleDotBorderWidthPx}px solid ${dark ? "rgba(255, 255, 255, 0.8)" : "#1a1a1a"}`,
                transition: CAPABILITY_METER_STYLE.dotTransitionCss,
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

// The "ladder card" capability tooltip (capabilityCard="ladder"): the card
// body is the in-canvas takeover's exact block — CapabilityLadder from
// capabilityHoverSwap.tsx (title + subtitle, speedup top-right, vertical-rail
// level list with the highlighted current row). Copy and row treatment edit
// there (CAPABILITY_HOVER_COPY / CAPABILITY_LEVEL_LADDER); these knobs are
// just the card's footprint.
export const CAPABILITY_LADDER_CARD_STYLE = {
  widthPx: 430,
  // The card normally hangs up-LEFT of the hovered point (like the stock
  // tooltip); this wide it would run off the graph's left edge for early
  // points, so within this distance of the left edge it hangs up-RIGHT.
  flipToRightWithinPx: 460,
}

// The "ladder-names" card (capabilityCard="ladder-names"): the ladder with
// definitions cut. Names only on the vertical rail; the current row keeps the
// highlight pill, the red dot, AND its one-liner (the only description on the
// card — it appears under the highlighted name and travels with it). Speedup
// closes the card as a hairline-separated stat row.
export const CAPABILITY_LADDER_NAMES_CARD_STYLE = {
  widthPx: 236,
  titleFontSize: "11.5px",
  rowFontSize: "9.5px",
  rowLineHeight: "16px",
  currentDescriptionFontSize: "9px",
  dotColumnWidthPx: 12,
  dotSizePx: 5,
  railColor: "rgba(0, 0, 0, 0.15)",
  currentDotColor: "var(--accent)",
  idleDotColor: "rgba(0, 0, 0, 0.3)",
  currentRowBackground: "rgba(0, 0, 0, 0.05)",
  idleNameOpacity: 0.65,
  currentDescriptionOpacity: 0.8,
  speedupValueFontSize: "13px",
  flipToRightWithinPx: 266,
}

// The "mini" card (capabilityCard="mini"): the glance tooltip — current level
// name in bold, the horizontal dot meter locating it on the ladder, and the
// speedup stat. No definitions at all.
export const CAPABILITY_MINI_CARD_STYLE = {
  widthPx: 200,
  levelFontSize: "12.5px",
  flipToRightWithinPx: 230,
}

// The mini card's MINIMAL meter (capabilityCard="mini-minimal"): small filled
// dots on a hairline rail — no hollow borders, no chunk. The current level
// reads from color (and a touch of size) alone.
export const CAPABILITY_MINIMAL_METER_STYLE = {
  dotSizePx: 4,
  currentDotSizePx: 6,
  idleDotColor: "rgba(0, 0, 0, 0.25)",
  currentDotColor: "var(--accent)",
  railColor: "rgba(0, 0, 0, 0.12)",
  railHeightPx: 1,
  // Same smooth current-state handoff between dots as the chunky meter.
  dotTransitionCss:
    "width 200ms ease-out, height 200ms ease-out, background 200ms ease-out",
}

// The INK meter (capabilityCard="mini-ink"): the minimal meter with no gray
// anywhere — solid black idle dots on a solid black hairline rail, the
// current level in red (black & red only, per the design style guide).
export const CAPABILITY_INK_METER_STYLE = {
  dotSizePx: 4,
  currentDotSizePx: 6,
  idleDotColor: "#1a1a1a",
  currentDotColor: "var(--accent)",
  railColor: "#1a1a1a",
  railHeightPx: 1,
  dotTransitionCss:
    "width 200ms ease-out, height 200ms ease-out, background 200ms ease-out",
}

// The "ladder-names-steady" card (capabilityCard="ladder-names-steady"):
// tab 4's concept rebuilt so nothing wobbles while the card follows the
// mouse. Every row is exactly rowHeightPx tall and the current level's
// one-liner renders in a reserved descriptionSlotHeightPx slot below the
// ladder — the card's geometry never changes on hover. The highlight pill is
// one absolutely-positioned element whose `top` transitions in the card's
// own coordinates (plain CSS), so the card moving with the pointer can't
// perturb it.
export const CAPABILITY_LADDER_STEADY_CARD_STYLE = {
  widthPx: 236,
  titleFontSize: "11.5px",
  rowHeightPx: 16,
  rowFontSize: "9.5px",
  // Fits the longest one-liner at two lines; constant so the card never
  // grows or shrinks as the level changes.
  descriptionSlotHeightPx: 28,
  descriptionFontSize: "9px",
  dotColumnWidthPx: 12,
  dotSizePx: 5,
  railColor: "rgba(0, 0, 0, 0.15)",
  currentDotColor: "var(--accent)",
  idleDotColor: "rgba(0, 0, 0, 0.3)",
  currentRowBackground: "rgba(0, 0, 0, 0.05)",
  idleNameOpacity: 0.65,
  descriptionOpacity: 0.8,
  speedupValueFontSize: "13px",
  pillTransitionCss: "top 180ms ease-out, opacity 180ms ease-out",
  rowTransitionCss: "opacity 180ms ease-out, font-weight 180ms ease-out",
  dotTransitionCss: "background 180ms ease-out",
  flipToRightWithinPx: 266,
}

// The "ladder-names-clean" card (capabilityCard="ladder-names-clean"): tab
// 4's layout — names-only rail, the current level's one-liner inline under
// its name — with the gray highlight pill removed. The current level reads
// from the red dot + bold name alone. No motion layout animation: weight and
// dot color transition in place, the description swaps without animating, so
// there is nothing to wobble while the card follows the pointer.
export const CAPABILITY_LADDER_CLEAN_CARD_STYLE = {
  widthPx: 236,
  titleFontSize: "11.5px",
  rowFontSize: "9.5px",
  rowLineHeight: "16px",
  currentDescriptionFontSize: "9px",
  dotColumnWidthPx: 12,
  dotSizePx: 5,
  railColor: "rgba(0, 0, 0, 0.15)",
  currentDotColor: "var(--accent)",
  idleDotColor: "rgba(0, 0, 0, 0.3)",
  idleNameOpacity: 0.65,
  currentDescriptionOpacity: 0.8,
  speedupValueFontSize: "13px",
  rowTransitionCss: "opacity 180ms ease-out, font-weight 180ms ease-out",
  dotTransitionCss: "background 180ms ease-out",
  flipToRightWithinPx: 266,
}

// Body of the clean ladder-names card — see
// CAPABILITY_LADDER_CLEAN_CARD_STYLE above.
const CapabilityLadderCleanContent = ({
  point,
}: {
  point: PointHoverTooltipPayload
}) => {
  const S = CAPABILITY_LADDER_CLEAN_CARD_STYLE
  return (
    <div className="px-3 py-2.5">
      <div className="font-bold" style={{ fontSize: S.titleFontSize }}>
        {CAPABILITY_HOVER_COPY.title}
      </div>
      <div className="relative mt-1.5">
        {/* the rail connecting the level dots, inset past the first/last dot */}
        <div
          className="absolute"
          style={{
            left: S.dotColumnWidthPx / 2,
            top: 8,
            bottom: 8,
            width: 1,
            background: S.railColor,
          }}
        />
        {CAPABILITY_LEVEL_LADDER.map((level) => {
          const isCurrent = level.label === point.capabilityThresholdLabel
          return (
            <div key={level.label} className="relative flex gap-1.5">
              <div
                className="shrink-0 flex justify-center"
                style={{ width: S.dotColumnWidthPx }}
              >
                <span
                  className="rounded-full"
                  style={{
                    width: S.dotSizePx,
                    height: S.dotSizePx,
                    marginTop: 5.5,
                    background: isCurrent ? S.currentDotColor : S.idleDotColor,
                    transition: S.dotTransitionCss,
                  }}
                />
              </div>
              <div className="min-w-0">
                <div
                  className="whitespace-nowrap"
                  style={{
                    fontSize: S.rowFontSize,
                    lineHeight: S.rowLineHeight,
                    fontWeight: isCurrent ? 700 : 400,
                    opacity: isCurrent ? 1 : S.idleNameOpacity,
                    transition: S.rowTransitionCss,
                  }}
                >
                  {level.label}
                </div>
                {isCurrent && (
                  <div
                    className="font-sans pb-[3px]"
                    style={{
                      fontSize: S.currentDescriptionFontSize,
                      lineHeight: 1.45,
                      opacity: S.currentDescriptionOpacity,
                    }}
                  >
                    {level.description}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <div className="border-t border-black/20 mt-2 pt-1.5">
        <PanelStatRow
          label={AI_CAPABILITIES_PANEL_COPY.speedupLabel}
          value={point.speedupText}
          valueFontSize={S.speedupValueFontSize}
        />
      </div>
    </div>
  )
}

// Body of the steady ladder-names card — see
// CAPABILITY_LADDER_STEADY_CARD_STYLE above for the mechanism.
const CapabilityLadderSteadyContent = ({
  point,
}: {
  point: PointHoverTooltipPayload
}) => {
  const S = CAPABILITY_LADDER_STEADY_CARD_STYLE
  const currentIndex = CAPABILITY_LEVEL_LADDER.findIndex(
    (l) => l.label === point.capabilityThresholdLabel,
  )
  return (
    <div className="px-3 py-2.5">
      <div className="font-bold" style={{ fontSize: S.titleFontSize }}>
        {CAPABILITY_HOVER_COPY.title}
      </div>
      <div
        className="relative mt-1.5"
        style={{ height: CAPABILITY_LEVEL_LADDER.length * S.rowHeightPx }}
      >
        {/* the highlight pill: one element, card-local CSS top transition */}
        <div
          className="absolute left-0 right-0 rounded-[2px]"
          style={{
            top: Math.max(currentIndex, 0) * S.rowHeightPx,
            height: S.rowHeightPx,
            background: S.currentRowBackground,
            opacity: currentIndex >= 0 ? 1 : 0,
            transition: S.pillTransitionCss,
          }}
        />
        {/* the rail connecting the level dots, inset past the first/last dot */}
        <div
          className="absolute"
          style={{
            left: S.dotColumnWidthPx / 2,
            top: S.rowHeightPx / 2,
            bottom: S.rowHeightPx / 2,
            width: 1,
            background: S.railColor,
          }}
        />
        {CAPABILITY_LEVEL_LADDER.map((level) => {
          const isCurrent = level.label === point.capabilityThresholdLabel
          return (
            <div
              key={level.label}
              className="relative flex items-center gap-1.5"
              style={{ height: S.rowHeightPx }}
            >
              <div
                className="shrink-0 flex justify-center"
                style={{ width: S.dotColumnWidthPx }}
              >
                <span
                  className="rounded-full"
                  style={{
                    width: S.dotSizePx,
                    height: S.dotSizePx,
                    background: isCurrent ? S.currentDotColor : S.idleDotColor,
                    transition: S.dotTransitionCss,
                  }}
                />
              </div>
              <div
                className="whitespace-nowrap"
                style={{
                  fontSize: S.rowFontSize,
                  fontWeight: isCurrent ? 700 : 400,
                  opacity: isCurrent ? 1 : S.idleNameOpacity,
                  transition: S.rowTransitionCss,
                }}
              >
                {level.label}
              </div>
            </div>
          )
        })}
      </div>
      {/* reserved description slot — constant height, text swaps in place */}
      <div
        className="font-sans mt-1"
        style={{
          height: S.descriptionSlotHeightPx,
          fontSize: S.descriptionFontSize,
          lineHeight: 1.45,
          opacity: S.descriptionOpacity,
        }}
      >
        {currentIndex >= 0 && CAPABILITY_LEVEL_LADDER[currentIndex].description}
      </div>
      <div className="border-t border-black/20 pt-1.5">
        <PanelStatRow
          label={AI_CAPABILITIES_PANEL_COPY.speedupLabel}
          value={point.speedupText}
          valueFontSize={CAPABILITY_LADDER_STEADY_CARD_STYLE.speedupValueFontSize}
        />
      </div>
    </div>
  )
}

// Shared point-anchoring wrapper for the capability cards: hangs the card
// up-left of the hovered point (the stock tooltip's transform); pass
// flipWithinPx to hang up-RIGHT instead when the point is within that
// distance of the graph's left edge (wide cards would run off the frame).
const PointAnchoredCard = ({
  point,
  flipWithinPx,
  children,
}: {
  point: PointHoverTooltipPayload
  flipWithinPx?: number
  children: ReactNode
}) => {
  const hangsRight = flipWithinPx !== undefined && point.cx < flipWithinPx
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: point.cx,
        top: point.cy - 7,
        transform: hangsRight
          ? "translate(6px, -100%)"
          : "translate(calc(-100% - 6px), -100%)",
      }}
    >
      {children}
    </div>
  )
}

// Body of the ladder-names card — see CAPABILITY_LADDER_NAMES_CARD_STYLE.
// Same motion treatment as the takeover's ladder: the highlight pill glides
// between rows, and since the current row is taller (its one-liner renders
// inside it), the pill stretches on the same clock.
const CapabilityLadderNamesContent = ({
  point,
}: {
  point: PointHoverTooltipPayload
}) => {
  const layoutGroupId = useId()
  return (
    <div className="px-3 py-2.5">
      <div
        className="font-bold"
        style={{ fontSize: CAPABILITY_LADDER_NAMES_CARD_STYLE.titleFontSize }}
      >
        {CAPABILITY_HOVER_COPY.title}
      </div>
      <LayoutGroup id={layoutGroupId}>
        <div className="relative mt-1.5">
          {/* the rail connecting the level dots, inset past the first/last dot */}
          <div
            className="absolute"
            style={{
              left: CAPABILITY_LADDER_NAMES_CARD_STYLE.dotColumnWidthPx / 2,
              top: 8,
              bottom: 8,
              width: 1,
              background: CAPABILITY_LADDER_NAMES_CARD_STYLE.railColor,
            }}
          />
          {CAPABILITY_LEVEL_LADDER.map((level) => {
            const isCurrent = level.label === point.capabilityThresholdLabel
            return (
              <div key={level.label} className="relative flex gap-1.5 py-[1px] pr-1">
                {isCurrent && (
                  <motion.div
                    layoutId="capability-ladder-names-current-row"
                    className="absolute inset-0 rounded-[2px]"
                    style={{
                      background:
                        CAPABILITY_LADDER_NAMES_CARD_STYLE.currentRowBackground,
                    }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  />
                )}
                <div
                  className="relative shrink-0 flex justify-center"
                  style={{ width: CAPABILITY_LADDER_NAMES_CARD_STYLE.dotColumnWidthPx }}
                >
                  <span
                    className="rounded-full"
                    style={{
                      width: CAPABILITY_LADDER_NAMES_CARD_STYLE.dotSizePx,
                      height: CAPABILITY_LADDER_NAMES_CARD_STYLE.dotSizePx,
                      marginTop: 5.5,
                      background: isCurrent
                        ? CAPABILITY_LADDER_NAMES_CARD_STYLE.currentDotColor
                        : CAPABILITY_LADDER_NAMES_CARD_STYLE.idleDotColor,
                      transition: "background 200ms ease-out",
                    }}
                  />
                </div>
                <div className="relative min-w-0">
                  <div
                    className="whitespace-nowrap"
                    style={{
                      fontSize: CAPABILITY_LADDER_NAMES_CARD_STYLE.rowFontSize,
                      lineHeight: CAPABILITY_LADDER_NAMES_CARD_STYLE.rowLineHeight,
                      fontWeight: isCurrent ? 700 : 400,
                      opacity: isCurrent
                        ? 1
                        : CAPABILITY_LADDER_NAMES_CARD_STYLE.idleNameOpacity,
                      transition: "opacity 200ms ease-out, font-weight 200ms ease-out",
                    }}
                  >
                    {level.label}
                  </div>
                  {isCurrent && (
                    <div
                      className="relative font-sans pb-[3px]"
                      style={{
                        fontSize:
                          CAPABILITY_LADDER_NAMES_CARD_STYLE.currentDescriptionFontSize,
                        lineHeight: 1.45,
                        opacity:
                          CAPABILITY_LADDER_NAMES_CARD_STYLE.currentDescriptionOpacity,
                      }}
                    >
                      {level.description}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </LayoutGroup>
      <div className="border-t border-black/20 mt-2 pt-1.5">
        <PanelStatRow
          label={AI_CAPABILITIES_PANEL_COPY.speedupLabel}
          value={point.speedupText}
          valueFontSize={CAPABILITY_LADDER_NAMES_CARD_STYLE.speedupValueFontSize}
        />
      </div>
    </div>
  )
}

// The minimal meter — same geometry as CapabilityLevelMeter (one dot per
// ladder level, evenly spread on a rail), quieter treatment. The render site
// passes the style dict (CAPABILITY_MINIMAL_METER_STYLE or
// CAPABILITY_INK_METER_STYLE), so cmd-clicking the dict name at the call
// reaches the editable literals.
const CapabilityMinimalMeter = ({
  point,
  meterStyle,
}: {
  point: PointHoverTooltipPayload
  meterStyle: typeof CAPABILITY_MINIMAL_METER_STYLE
}) => {
  const levelIndex = CAPABILITY_LEVEL_LADDER.findIndex(
    (l) => l.label === point.capabilityThresholdLabel,
  )
  const dotSize = meterStyle.dotSizePx
  return (
    <div
      className="relative mt-2"
      style={{ height: meterStyle.currentDotSizePx }}
    >
      <div
        className="absolute top-1/2 -translate-y-1/2"
        style={{
          left: dotSize / 2,
          right: dotSize / 2,
          height: meterStyle.railHeightPx,
          background: meterStyle.railColor,
        }}
      />
      <div className="absolute inset-0 flex items-center justify-between">
        {CAPABILITY_LEVEL_LADDER.map((level, i) => {
          const isCurrent = i === levelIndex
          return (
            <span
              key={level.label}
              className="rounded-full"
              style={{
                width: isCurrent ? meterStyle.currentDotSizePx : dotSize,
                height: isCurrent ? meterStyle.currentDotSizePx : dotSize,
                background: isCurrent
                  ? meterStyle.currentDotColor
                  : meterStyle.idleDotColor,
                transition: meterStyle.dotTransitionCss,
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

// The AI-capabilities panel that follows the hovered point along the line —
// same anchor transform as the stock point tooltip (box hangs up-left of the
// point), so it moves with the mouse exactly like today. capabilityCard on
// <UnifiedHudPanels> picks which of the five designs renders.
export const UnifiedCapabilityPointPanel = ({
  point,
}: {
  point: PointHoverTooltipPayload
}) => {
  const design = useUnifiedHudPanels()?.capabilityCard ?? "meter"
  if (design === "ladder") {
    return (
      <PointAnchoredCard
        point={point}
        flipWithinPx={CAPABILITY_LADDER_CARD_STYLE.flipToRightWithinPx}
      >
        <HudHoverPanel
          widthPx={CAPABILITY_LADDER_CARD_STYLE.widthPx}
          showDetailsFooter
        >
          <div className="px-3 py-2.5">
            <CapabilityLadder point={point} />
          </div>
        </HudHoverPanel>
      </PointAnchoredCard>
    )
  }
  if (design === "ladder-names") {
    return (
      <PointAnchoredCard
        point={point}
        flipWithinPx={CAPABILITY_LADDER_NAMES_CARD_STYLE.flipToRightWithinPx}
      >
        <HudHoverPanel
          widthPx={CAPABILITY_LADDER_NAMES_CARD_STYLE.widthPx}
          showDetailsFooter
        >
          <CapabilityLadderNamesContent point={point} />
        </HudHoverPanel>
      </PointAnchoredCard>
    )
  }
  if (design === "ladder-names-steady") {
    return (
      <PointAnchoredCard
        point={point}
        flipWithinPx={CAPABILITY_LADDER_STEADY_CARD_STYLE.flipToRightWithinPx}
      >
        <HudHoverPanel
          widthPx={CAPABILITY_LADDER_STEADY_CARD_STYLE.widthPx}
          showDetailsFooter
        >
          <CapabilityLadderSteadyContent point={point} />
        </HudHoverPanel>
      </PointAnchoredCard>
    )
  }
  if (design === "ladder-names-clean") {
    return (
      <PointAnchoredCard
        point={point}
        flipWithinPx={CAPABILITY_LADDER_CLEAN_CARD_STYLE.flipToRightWithinPx}
      >
        <HudHoverPanel
          widthPx={CAPABILITY_LADDER_CLEAN_CARD_STYLE.widthPx}
          showDetailsFooter
        >
          <CapabilityLadderCleanContent point={point} />
        </HudHoverPanel>
      </PointAnchoredCard>
    )
  }
  if (design === "mini" || design === "mini-minimal" || design === "mini-ink") {
    return (
      <PointAnchoredCard
        point={point}
        flipWithinPx={CAPABILITY_MINI_CARD_STYLE.flipToRightWithinPx}
      >
        <HudHoverPanel
          title={AI_CAPABILITIES_PANEL_COPY.title}
          widthPx={CAPABILITY_MINI_CARD_STYLE.widthPx}
          showDetailsFooter
        >
          <div className="px-3 py-2.5">
            <div
              className="font-bold"
              style={{ fontSize: CAPABILITY_MINI_CARD_STYLE.levelFontSize }}
            >
              {point.capabilityThresholdLabel}
            </div>
            {design === "mini-minimal" ? (
              <CapabilityMinimalMeter
                point={point}
                meterStyle={CAPABILITY_MINIMAL_METER_STYLE}
              />
            ) : design === "mini-ink" ? (
              <CapabilityMinimalMeter
                point={point}
                meterStyle={CAPABILITY_INK_METER_STYLE}
              />
            ) : (
              <CapabilityLevelMeter point={point} />
            )}
            <div className="mt-2.5">
              <PanelStatRow
                label={AI_CAPABILITIES_PANEL_COPY.speedupLabel}
                value={point.speedupText}
              />
            </div>
          </div>
        </HudHoverPanel>
      </PointAnchoredCard>
    )
  }
  return (
    <PointAnchoredCard point={point}>
      <HudHoverPanel
        title={AI_CAPABILITIES_PANEL_COPY.title}
        widthPx={280}
        showDetailsFooter
      >
        <div className="px-3 py-2.5">
          <PanelStatRow
            label={AI_CAPABILITIES_PANEL_COPY.speedupLabel}
            value={point.speedupText}
            valueFontSize={CAPABILITY_METER_STYLE.speedupValueFontSize}
          />
          <div className="mt-1">
            <PanelBodyText mono>
              {AI_CAPABILITIES_PANEL_COPY.speedupDescription}
            </PanelBodyText>
          </div>
          <div className="mt-3">
            <PanelStatRow
              label={AI_CAPABILITIES_PANEL_COPY.levelLabel}
              value={point.capabilityThresholdLabel}
            />
          </div>
          <CapabilityLevelMeter point={point} />
        </div>
      </HudHoverPanel>
    </PointAnchoredCard>
  )
}

// The takeover-risk panel, anchored where the band danger label sits today
// (left edge of the hovered band) and fading with the same opacity signal.
// The band's live risk text leads the body.
export const UnifiedTakeoverRiskPanel = ({
  label,
}: {
  label: BandDangerLabelPayload
}) => (
  <div
    className="absolute pointer-events-none"
    style={{
      left: label.anchor.x + 8,
      top: label.anchor.y,
      transform: "translateY(-50%)",
      opacity: label.opacity,
      transition: "opacity 150ms ease",
    }}
  >
    <HudHoverPanel title={AI_TAKEOVER_RISK_PANEL_COPY.title} widthPx={280}>
      <div className="px-3 py-2.5">
        <div
          className="font-bold uppercase tracking-wide mb-1"
          style={{ fontSize: "10px" }}
        >
          {label.text}
        </div>
        <PanelBodyText>{AI_TAKEOVER_RISK_PANEL_COPY.body}</PanelBodyText>
      </div>
    </HudHoverPanel>
  </div>
)

// Unit column header for the power breakdown — the underlying data is
// H100-equivalents (see powerBubbleChartData.ts).
export const POWER_PANEL_UNIT_HEADER = "H100 equiv."

// One row of the power breakdown: optional flag icon, name, right-aligned
// value. The row matching the hovered treemap cell reads bold in the accent
// color (like the mockup's "Rest of US").
const PowerPanelRow = ({
  icon: Icon,
  name,
  value,
  emphasized = false,
  highlighted = false,
  indented = false,
}: {
  icon?: React.ComponentType<{ size?: number; color?: string }>
  name: string
  value: string
  emphasized?: boolean
  highlighted?: boolean
  indented?: boolean
}) => (
  <div
    className="flex items-baseline justify-between gap-3 py-[1.5px]"
    style={{
      paddingLeft: indented ? 16 : 0,
      fontWeight: emphasized || highlighted ? 700 : 400,
      color: highlighted ? "var(--accent)" : undefined,
      fontSize: "10.5px",
    }}
  >
    <span className="flex items-center gap-1.5 min-w-0">
      {Icon && <Icon size={10} color="currentColor" />}
      <span className="whitespace-nowrap overflow-hidden text-ellipsis">{name}</span>
    </span>
    <span className="whitespace-nowrap tabular-nums">{value}</span>
  </div>
)

// Body of the DISTRIBUTION OF POWER panel, per the mockup: a NAME /
// H100 EQUIV. column header, then each region (flag, bold name, bold total)
// with its labs indented beneath — the hovered cell's row in the accent color.
export const PowerBreakdownPanelContent = ({
  regions,
  hoveredItem,
}: {
  regions: PowerRegionData[]
  hoveredItem: HoveredPowerItem | null
}) => {
  const hoveredRegionId = getHoveredRegionId(hoveredItem, regions)
  return (
    <div className="px-3 py-2">
      <div
        className="flex items-baseline justify-between gap-3 uppercase tracking-wide opacity-60 font-bold mb-1"
        style={{ fontSize: "8.5px" }}
      >
        <span>Name</span>
        <span>{POWER_PANEL_UNIT_HEADER}</span>
      </div>
      {regions.map((region) => (
        <div key={region.id} className="mb-1.5 last:mb-0">
          <PowerPanelRow
            icon={regionIconById[region.id]}
            name={region.name}
            value={formatCompact(region.computeStock)}
            emphasized
            highlighted={hoveredItem?.kind === "region" && hoveredItem.id === region.id}
          />
          {region.id === hoveredRegionId &&
            getLabsWorthShowing(region).map((lab) => (
              <PowerPanelRow
                key={lab.id}
                name={lab.name}
                value={formatCompact(lab.compute)}
                highlighted={hoveredItem?.kind === "lab" && hoveredItem.id === lab.id}
                indented
              />
            ))}
        </div>
      ))}
    </div>
  )
}

// Layout knobs for the POLICIES card body. Headers stay solid black — smaller
// size (not gray/opacity) is what de-emphasizes them against the rows.
export const POLICIES_GROUP_PANEL_STYLE = {
  headerFontSize: "9px",
  rowFontSize: "10.5px",
  // Matches the mini square's 14px height so a wrapped label's first line
  // sits centered on its square.
  rowLineHeight: "14px",
}

// Body of the POLICIES panel (hovering the policies block, no specific
// square): every non-empty status as a section, chunked by hairline dividers.
// Each row leads with the policy's mini square in its own per-status
// treatment (red-filled = in discussion, outlined = not), the same glyphs as
// the panel boxes the pointer is on — so status reads per-row, not just from
// the section header.
export const PoliciesGroupPanelContent = ({
  grouped,
}: {
  grouped: PoliciesByStatus
}) => {
  const dark = useIsDarkPanelTheme()
  const lineClass = dark
    ? UNIFIED_PANEL_DARK_THEME.lineColorClass
    : "border-black/20"
  const sections = POLICY_STATUS_ORDER.filter(
    (status: PolicyStatus) => grouped[status].length > 0,
  )
  return (
    <div className="px-3 py-2">
      {sections.map((status, sectionIndex) => (
        <div
          key={status}
          className={sectionIndex > 0 ? `mt-2 pt-2 border-t ${lineClass}` : undefined}
        >
          <div
            className="uppercase tracking-wide font-bold mb-1"
            style={{ fontSize: POLICIES_GROUP_PANEL_STYLE.headerFontSize }}
          >
            {POLICY_STATUS_LABELS[status]}
          </div>
          {grouped[status].map((policy) => (
            <div
              key={policy.id}
              className="flex items-start gap-2 py-[2px]"
              style={{
                fontSize: POLICIES_GROUP_PANEL_STYLE.rowFontSize,
                lineHeight: POLICIES_GROUP_PANEL_STYLE.rowLineHeight,
              }}
            >
              <PolicyMiniSquare policy={policy} status={status} />
              <span className="min-w-0">{policy.label}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

// Body of the POLICY panel (hovering a specific square): big bold name +
// the layperson one-liner. The footer link (source reference) is added by
// the caller via HudHoverPanel's detailsHref.
export const PolicyDetailPanelContent = ({ policy }: { policy: Policy }) => (
  <div className="px-3 py-2.5">
    <div className="font-bold" style={{ fontSize: "14px", lineHeight: 1.3 }}>
      {policy.label}
    </div>
    {POLICY_DESCRIPTIONS[policy.id] && (
      <div className="mt-1">
        <PanelBodyText>{POLICY_DESCRIPTIONS[policy.id]}</PanelBodyText>
      </div>
    )}
  </div>
)

// A metric indicator's definition in panel form (Employment, Safety Tax, …).
// On this branch clicking the indicator really opens its metric-details
// panel (MetricTrigger), so the card carries the "Click for details" footer.
export const MetricDefinitionPanel = ({
  metricId,
}: {
  metricId: HoverableMetricId
}) => {
  const definition = METRIC_HOVER_DEFINITIONS[metricId]
  return (
    <HudHoverPanel title={definition.title} widthPx={260} showDetailsFooter>
      <div className="px-3 py-2.5">
        <PanelBodyText>{definition.definition}</PanelBodyText>
      </div>
    </HudHoverPanel>
  )
}
